// StudentAssessmentPivotTable.jsx
import React, { useMemo, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Divider,
  Stack,
  useTheme,
  ButtonBase,
  Popover,
  Popper,
  LinearProgress,
} from "@mui/material";
import { School } from "@mui/icons-material";
import AssessmentPivotPopover from "../Popover/AssessmentPivotPopover";
import API from "../../api/api";
import { setSnackError } from "../../Reducers/snackErrors";
import { useDispatch } from "react-redux";

/**
 * Props:
 *  - data: either an array of rows or { data: rows }
 *    row = {
 *      student_id, first_name, last_name,
 *      assessment_id, assessment_title,
 *      score, max_score
 *    }
 */
export default function StudentAssessmentPivotTable({ data }) {
  const rows = Array.isArray(data) ? data : Array.isArray(data) ? data : [];
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  const handleClick = async (event, session_id) => {
    console.log(session_id);
    setAnchorEl(anchorEl ? null : event.currentTarget);
    getPivotTableContent(session_id)
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSession(null);
  };


  const getPivotTableContent = async (sid) => {
    if (!sid) { return null; }
    try {
      setLoading(true)
      const response = await API.get('/get_assessment_content', {params: { session_id: sid }})
      if (!response.data.data) {
        setSession(null);
        return;
      }
      if (response.data.data) {
        setSession(response.data.data);
        return;
      }
    }
    catch(error) {
      if (error.isAuthError || error.isForbidden) {
          return;
        } 
        console.log(error)
        if (error.isNetworkError) {
          dispatch(setSnackError({ 
            title: 'Connection Error', 
            body: error.message 
          }));
        } else {
          dispatch(setSnackError({
            title: 'Error found',
            body: error.message || 'Unable to get programs.'
          }));
        }
      return;
    }
    finally {
      setLoading(false);
    }
  }



  // All unique assessments (id -> title), stable order by id then title
  const assessments = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      if (!map.has(r.assessment_id)) {
        map.set(r.assessment_id, r.assessment_title);
      }
    }
    return Array.from(map.entries())
      .sort((a, b) => (a[0] - b[0]) || String(a[1]).localeCompare(String(b[1])))
      .map(([id, title]) => ({ id, title }));
  }, [rows]);

  // Group by student: { student_id: { first, last, byAssessment: { [assessment_id]: {score,max,pct} } } }
  const students = useMemo(() => {
    const m = new Map();
    for (const r of rows) {
      const key = r.student_id;
      if (!m.has(key)) {
        m.set(key, {
          student_id: r.student_id,
          first_name: r.first_name?.trim() ?? "",
          last_name: r.last_name?.trim() ?? "",
          session_id: r.session_id,
          byAssessment: {},
        });
      }
      const s = m.get(key);
      const pct =
        r && r.max_score > 0 ? Math.round((Number(r.score) / Number(r.max_score)) * 1000) / 10 : null;
      s.byAssessment[r.assessment_id] = {
        score: r.score,
        max: r.max_score,
        pct, // null means can't compute
      };
    }
    // Sort students by last, first, then id
    return Array.from(m.values()).sort((a, b) => {
      const l = String(a.last_name).localeCompare(String(b.last_name));
      if (l !== 0) return l;
      const f = String(a.first_name).localeCompare(String(b.first_name));
      if (f !== 0) return f;
      return (a.student_id ?? 0) - (b.student_id ?? 0);
    });
  }, [rows]);

  console.log(students)
  
  return (
    <Box sx={{ p: 1 }}>
      {/* Header */}
      <Box sx={{ mb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Student assessment matrix
            </Typography>
            <Typography variant="body2" color="text.secondary">
              &nbsp;Columns are assessments; cells show score and normalized %
            </Typography>
          </Box>
        </Stack>
        <Divider sx={{ mt: 1 }} />
      </Box>

      {/* Table */}
      <Paper variant="outlined" sx={{ borderRadius: 2 }}>
        <TableContainer sx={{ maxHeight: "70vh" }}>
          <Table stickyHeader size="small" aria-label="Student assessment pivot">
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 120 }}>
                  <Typography variant="subtitle2">First name</Typography>
                </TableCell>
                <TableCell sx={{ minWidth: 120 }}>
                  <Typography variant="subtitle2">Last name</Typography>
                </TableCell>
                {/* Dynamic assessment columns */}
                {assessments.map((a) => (
                  <TableCell key={a.id} align="center" sx={{ minWidth: 160 }}>
                    <Typography variant="subtitle1">{a.title}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      (ID: {a.id})
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {students.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2 + assessments.length}>
                    <Box
                      sx={{
                        py: 6,
                        display: "grid",
                        placeItems: "center",
                        color: "text.secondary",
                      }}
                    >
                      <Typography variant="body2">No data to display</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                students.map((s) => (
                  <TableRow key={s.student_id} hover>
                    <TableCell>
                      <Typography variant="body2">{s.first_name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{s.last_name}</Typography>
                    </TableCell>

                    {assessments && assessments.map((a) => {
                      const cell = s.byAssessment[a.id];
                      if (!cell) {
                        return (
                          <TableCell key={a.id} align="center" sx={{ color: "text.disabled" }}>
                            <Typography variant="caption">Missing</Typography>
                          </TableCell>
                        );
                      }
                      return (
                        <TableCell key={a.id} align="center" sx={{ px: 1.5, py: 0.75 }}>
                            <ButtonBase
                                aria-describedby={id}
                                onClick={async(e) => handleClick(e, s.session_id)}
                                sx={{
                                    width: '100%',
                                    justifyContent: 'center',
                                    borderRadius: 1,
                                    p: 0.5,
                                    transition: 'background-color .15s',
                                    '&:hover': { bgcolor: 'action.hover' },
                                    '&:focus-visible': { outline: (t) => `2px solid ${t.palette.primary.main}` }
                                }}
                            >
                            <Box
                                sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 0.25,
                                }}
                            >
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                {/* Vertical marker */}
                                <Box
                                    sx={{
                                    width: 4,
                                    height: 24,
                                    borderRadius: 1,
                                    bgcolor:
                                        cell.pct == null
                                        ? "grey.400"
                                        : cell.pct < 60
                                        ? "error.main"
                                        : cell.pct < 70
                                        ? "warning.main"
                                        : cell.pct < 80
                                        ? "success.light"
                                        : "success.main",
                                    }}
                                />
                                {/* Score text */}
                                <Typography variant="body2" fontWeight={600}>
                                    {cell.score}/{cell.max}
                                </Typography>
                                </Box>
                                {/* Normalized % */}
                                <Typography
                                variant="caption"
                                sx={{
                                    color:
                                    cell.pct == null
                                        ? "text.disabled"
                                        : cell.pct < 60
                                        ? "error.main"
                                        : cell.pct < 70
                                        ? "warning.main"
                                        : cell.pct < 80
                                        ? "success.light"
                                        : "success.main",
                                }}
                                >
                                {cell.pct === null ? "—" : `${cell.pct}%`}
                                </Typography>
                            </Box>
                            </ButtonBase>
                            <Popper id={id} open={open} anchorEl={anchorEl} placement="right-start">
                              <AssessmentPivotPopover data={session} loading={loading} />
                            </Popper>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Footnote */}
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
        Normalized score = <i>score / max_score × 100</i> (shown to 0.1% where applicable).
      </Typography>
    </Box>
  );
}